import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const PersonnelDashboard = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">What the dashboard is for</Typography>
      <Typography>
        The workforce dashboard at{' '}
        <DocLink url="/employeesdashboard" text="People > Dashboard" /> gives
        personnel managers a single view of headcount, compliance, and
        everything that currently needs a decision — so the day starts with
        the actions, not with a hunt through separate lists.
      </Typography>
      <Alert severity="info" variant="outlined">
        The dashboard requires the <strong>Personnel</strong> team. Site
        administrators always have access.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Headline tiles</Typography>
      <Typography>
        Six stat tiles sit under the header, each linking straight to the
        relevant page:
      </Typography>
      <ul style={{ marginTop: 0 }}>
        <li>
          <strong>Active staff</strong> — current headcount, with a hint
          showing recent new starters. Opens{' '}
          <DocLink url="/employees" text="Employees" />.
        </li>
        <li>
          <strong>New applicants</strong> — applications awaiting a decision.
          Opens <DocLink url="/employees/applications" text="Applications" />.
        </li>
        <li>
          <strong>DBS out of date</strong> — staff needing a new check. Opens{' '}
          <DocLink url="/dbs-checks" text="DBS Checks" />.
        </li>
        <li>
          <strong>Training uploads</strong> — certificates submitted by staff
          that need approving. Opens{' '}
          <DocLink url="/pending-mandatory-training" text="Pending Mandatory Training" />
          .
        </li>
        <li>
          <strong>Appraisals</strong> — open appraisals, with a hint showing
          how many are still waiting for the staff member. Opens{' '}
          <DocLink url="/appraisals/pending-review" text="Open Appraisals" />.
        </li>
        <li>
          <strong>Open actions</strong> — the total across every Action Centre
          category.
        </li>
      </ul>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Compliance and workforce charts</Typography>
      <Typography>
        The main panel shows compliance pulse rings for mandatory training,
        DBS checks, policy acknowledgement, and occupational health screening,
        plus per-course compliance, the mix of clinical roles across the
        workforce, and a trend of new starters by month — useful for spotting
        a course that's quietly slipping or a recruitment gap building up.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Action Centre</Typography>
      <Typography>
        The Action Centre lists everything needing attention, grouped by
        category with counts and severity:
      </Typography>
      <ul style={{ marginTop: 0 }}>
        <li>Staff with expired mandatory training</li>
        <li>Staff without an in-date DBS</li>
        <li>Expired driving licences</li>
        <li>Overdue driving licence checks</li>
        <li>Staff without an ID card</li>
        <li>Training uploads to review</li>
        <li>
          Open appraisals — both waiting for staff and awaiting HR review,
          with each person labelled accordingly
        </li>
        <li>Staff with an outstanding occupational health screening</li>
      </ul>
      <Typography>
        Each entry links to the place where the action is completed — an
        employee's profile, the DBS dashboard, the training review queue, or
        the appraisal review queue.
      </Typography>
      <Alert severity="info" variant="outlined">
        Each worklist loads independently, so if one category can't be fetched
        the rest of the dashboard still works — that category is simply hidden
        rather than taking the page down.
      </Alert>
    </Stack>
  </Stack>
)

export default PersonnelDashboard
